import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { RouteErrorBoundary } from "@/components/ErrorBoundary";
import { SkipToContent } from "@/components/shared";
import { AnimatedRoutes } from "@/components/layout/AnimatedRoutes";
import { LoadingScreen } from "@/components/layout/LoadingScreen";

import { usePrefetchProducts } from "@/hooks/usePrefetchProducts";
import { useLanguageStore } from "@/stores/language";
import { isFirstVisit, detectLanguageFromGeo, cleanLangParam } from "@/utils/detectLanguage";
import { useState, useEffect, lazy, Suspense } from "react";

// Lazy load Sonner toaster - only needed when showing notifications
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const AppContent = () => {
  
  // Prefetch Shopify products early to break network dependency chain
  usePrefetchProducts();

  // Sync document lang attribute with language store
  const language = useLanguageStore((s) => s.language);
  const setLanguage = useLanguageStore((s) => s.setLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Phase 2: Geo-correct language on first visit only
  useEffect(() => {
    if (!isFirstVisit()) return;

    let cancelled = false;
    detectLanguageFromGeo().then(geoLang => {
      if (cancelled || !geoLang) return;
      if (geoLang !== language) {
        setLanguage(geoLang);
      }
    });

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean ?lang= param from URL after consumption
  useEffect(() => {
    cleanLangParam();
  }, []);
  
  return (
    <>
      <SkipToContent />
      <RouteErrorBoundary>
        <AnimatedRoutes />
      </RouteErrorBoundary>
    </>
  );
};

const App = () => {
  // FCP Optimization: Skip loading screen entirely - inline CSS in index.html 
  // provides immediate visual feedback while React hydrates
  const [showLoading, setShowLoading] = useState(false);

  // Mark session as visited for any future loading screen logic
  useEffect(() => {
    sessionStorage.setItem('hasVisited', 'true');
  }, []);

  // Language state is managed by Zustand store (useLanguageStore)
  // No React context provider needed - the store provides state directly to components
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="dark" forcedTheme="dark">
          <TooltipProvider>
            <Suspense fallback={null}>
              <Sonner />
            </Suspense>
            
            {showLoading && (
              <LoadingScreen onComplete={() => setShowLoading(false)} />
            )}
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <AppContent />
            </BrowserRouter>
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
